import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ── Design tokens — matches Landing ─────────────
const C = {
  bg: "#ffffff",
  surface: "#f7f7f7",
  peach: "#ECDBC9",
  grey: "#CDCDCD",
  border: "#e0e0e0",
  text: "#000000",
  muted: "#555555",
  dim: "#888888",
  accent: "#52658F",
  green: "#2a7a5a",
  cyan: "#2a6a8a",
};
const SYNE = "'Fraunces', serif";
const MONO = "'Plus Jakarta Sans', sans-serif";
const FONT_LINK = "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,700;9..144,800&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap";

const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
const px = isMobile ? "1.25rem" : "4rem";

// ── Contact Modal ─────────────────────────────────
function ContactModal({ plan, onClose }) {
    const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");

    async function handleSubmit() {
        if (!form.name || !form.email) { setError("Name and email are required."); return; }
        if (!form.email.includes("@")) { setError("Please enter a valid email."); return; }
        setLoading(true); setError("");
        try {
            const res = await fetch(process.env.REACT_APP_API_URL + "/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...form, plan }),
            });
            if (!res.ok) throw new Error("Failed");
            setSent(true);
        } catch {
            setError("Something went wrong. Email us directly at hello@datagetsbalanced.com");
        }
        setLoading(false);
    }

    const inputStyle = {
        background: "white", border: "1.5px solid " + C.border, borderRadius: 8,
        padding: "0.65rem 1rem", color: C.text, fontFamily: MONO, fontSize: "0.85rem",
        width: "100%", outline: "none", boxSizing: "border-box",
    };

    return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
            onClick={onClose}>
            <div style={{ background: "white", border: "1px solid " + C.border, borderRadius: 16, padding: "2rem", width: "100%", maxWidth: 480, position: "relative", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}
                onClick={e => e.stopPropagation()}>
                <button onClick={onClose} style={{ position: "absolute", top: "1rem", right: "1rem", background: "transparent", border: "1px solid " + C.border, borderRadius: 6, width: 28, height: 28, color: C.muted, cursor: "pointer", fontSize: "0.9rem" }}>✕</button>

                {sent ? (
                    <div style={{ textAlign: "center", padding: "1.5rem 0" }}>
                        <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>✓</div>
                        <div style={{ fontFamily: SYNE, fontWeight: 700, fontSize: "1.1rem", color: C.text, marginBottom: "0.5rem" }}>Message sent!</div>
                        <div style={{ color: C.muted, fontSize: "0.85rem", fontFamily: MONO }}>We'll get back to you within 1 business day.</div>
                        <button onClick={onClose} style={{ marginTop: "1.5rem", background: C.accent, color: "white", border: "none", padding: "0.65rem 1.5rem", borderRadius: 8, fontFamily: MONO, fontWeight: 700, cursor: "pointer" }}>Done</button>
                    </div>
                ) : (
                    <>
                        <div style={{ fontSize: "0.7rem", color: C.accent, fontFamily: MONO, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "0.5rem" }}>{plan.toUpperCase()} INQUIRY</div>
                        <h2 style={{ fontFamily: SYNE, fontWeight: 700, fontSize: "1.4rem", color: C.text, marginBottom: "0.4rem" }}>Get in touch</h2>
                        <p style={{ color: C.muted, fontSize: "0.85rem", marginBottom: "1.5rem", fontFamily: MONO }}>Tell us a bit about your business and we'll find the right plan for you.</p>

                        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                            {[
                                { label: "NAME *", key: "name", placeholder: "Your name", type: "text" },
                                { label: "EMAIL *", key: "email", placeholder: "you@company.com", type: "email" },
                                { label: "COMPANY", key: "company", placeholder: "Company name (optional)", type: "text" },
                            ].map(field => (
                                <div key={field.key}>
                                    <div style={{ fontSize: "0.7rem", color: C.dim, marginBottom: "0.3rem", fontFamily: MONO, fontWeight: 600, letterSpacing: "0.06em" }}>{field.label}</div>
                                    <input style={inputStyle} placeholder={field.placeholder} type={field.type} value={form[field.key]} onChange={e => setForm({ ...form, [field.key]: e.target.value })} />
                                </div>
                            ))}
                            <div>
                                <div style={{ fontSize: "0.7rem", color: C.dim, marginBottom: "0.3rem", fontFamily: MONO, fontWeight: 600, letterSpacing: "0.06em" }}>MESSAGE</div>
                                <textarea style={{ ...inputStyle, minHeight: 80, resize: "vertical" }} placeholder="Tell us about your reconciliation process and what you need..." value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} />
                            </div>
                        </div>

                        {error && <div style={{ marginTop: "0.75rem", fontSize: "0.78rem", color: "#c0392b" }}>{error}</div>}

                        <button onClick={handleSubmit} disabled={loading}
                            style={{ marginTop: "1.25rem", width: "100%", background: C.accent, color: "white", border: "none", padding: "0.8rem", borderRadius: 8, fontFamily: MONO, fontWeight: 700, fontSize: "0.9rem", cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}>
                            {loading ? "Sending..." : "Send message →"}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

const TIERS = [
    {
        id: "free",
        name: "Free",
        price: "Rp 0",
        period: "forever",
        accentColor: C.green,
        borderColor: C.border,
        bgColor: "white",
        badge: null,
        features: [
            "3 checks per month",
            "Up to 100 rows per file",
            "CSV & Excel upload",
            "Smart Setup — auto column detection",
            "Basic results table",
        ],
        notIncluded: [
            "AI explanation",
            "PDF export",
            "Google Sheets import",
            "Unlimited checks",
        ],
        cta: "Get started free",
        ctaVariant: "outline",
        href: "/app",
    },
    {
        id: "pro",
        name: "Pro",
        price: "Custom",
        period: "tailored for your business",
        accentColor: C.accent,
        borderColor: C.accent,
        bgColor: "#f0f3f8",
        badge: "MOST POPULAR",
        features: [
            "Unlimited checks",
            "Unlimited rows per file",
            "CSV, Excel & Google Sheets",
            "Full AI explanation per row",
            "PDF export with DGB branding",
            "Industry-specific AI language",
            "Priority support",
        ],
        notIncluded: [],
        cta: "Contact us →",
        ctaVariant: "filled",
    },
    {
        id: "enterprise",
        name: "Enterprise",
        price: "Custom",
        period: "for large teams & integrations",
        accentColor: C.cyan,
        borderColor: C.border,
        bgColor: "white",
        badge: null,
        features: [
            "Everything in Pro",
            "Multiple users & teams",
            "Custom integrations (ERP, core banking)",
            "Dedicated onboarding & training",
            "SLA guarantee",
            "Custom AI prompts for your industry",
            "White-label option",
        ],
        notIncluded: [],
        cta: "Contact us →",
        ctaVariant: "outline-accent",
    },
];

const FAQ = [
    { q: "What counts as a 'check'?", a: "One check = one reconciliation run. Uploading two files and clicking 'Check now' counts as one check." },
    { q: "What happens when I hit the free limit?", a: "You'll see a message letting you know you've used your 3 free checks for the month. Your data and history are safe — just contact us to upgrade." },
    { q: "Is my data safe?", a: "Yes. Files are processed in your browser and never stored on our servers. Only anonymized report summaries are saved for your history. AI explanations send only row-level summaries to Anthropic's API." },
    { q: "What industries does DGB support?", a: "DGB works for any industry — F&B, retail, banking, logistics, healthcare, government, and more. The AI explanation adapts its language to your industry context." },
    { q: "Can I use DGB for large files?", a: "Pro and Enterprise plans support unlimited rows. Files are processed in-browser so performance depends on your device, but we've tested with files up to 100,000 rows." },
    { q: "How does pricing work for Pro?", a: "Pro pricing is custom because businesses have different needs — number of users, check volume, integrations required. Contact us and we'll find something that works." },
];

export default function Pricing() {
    const navigate = useNavigate();
    const [contactPlan, setContactPlan] = useState(null);
    const [openFaq, setOpenFaq] = useState(null);

    useEffect(() => {
        if (!document.querySelector('link[href*="Fraunces"]')) {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = FONT_LINK;
            document.head.appendChild(link);
        }
    }, []);

    function handleCTA(tier) {
        if (tier.id === "free") navigate(tier.href);
        else setContactPlan(tier.id);
    }

    return (
        <div style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: MONO, overflowX: "hidden" }}>
            {contactPlan && <ContactModal plan={contactPlan} onClose={() => setContactPlan(null)} />}

            {/* Navbar */}
            <nav style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 2rem", background: "#000000", position: "sticky", top: 0, zIndex: 10 }}>
                <img src={require("../assets/dgb-logo-dark.png")} alt="DGB" style={{ height: 72, cursor: "pointer", marginLeft: "-8px" }} onClick={() => navigate("/")} />
                <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
                    <button onClick={() => navigate("/")} style={{ background: "transparent", border: "none", color: "#888", fontFamily: MONO, fontSize: "0.85rem", cursor: "pointer" }}>← Back</button>
                    <button onClick={() => navigate("/app")} style={{ background: C.accent, color: "white", border: "none", padding: "0.55rem 1.25rem", borderRadius: 8, fontFamily: MONO, fontWeight: 700, cursor: "pointer", fontSize: "0.85rem" }}>Try Free</button>
                </div>
            </nav>

            {/* Header */}
            <section style={{ textAlign: "center", padding: `4rem ${px} 3rem`, maxWidth: 680, margin: "0 auto" }}>
                <div style={{ display: "inline-block", background: C.peach, border: "1px solid #d8c4a8", color: "#5a3a1a", fontSize: "0.72rem", padding: "0.3rem 1rem", borderRadius: 100, marginBottom: "1.5rem", fontFamily: MONO, fontWeight: 700, letterSpacing: "0.08em" }}>
                    PRICING
                </div>
                <h1 style={{ fontFamily: SYNE, fontSize: "clamp(2rem,4.5vw,3rem)", fontWeight: 800, color: C.text, marginBottom: "1rem", lineHeight: 1.18, letterSpacing: "-0.02em" }}>
                    Simple, honest pricing
                </h1>
                <p style={{ color: C.muted, fontSize: "1rem", lineHeight: 1.8, fontFamily: MONO }}>
                    Start free — no credit card required. Upgrade when you need more.<br />
                    Pro and Enterprise pricing is tailored to your business.
                </p>
            </section>

            {/* Tiers */}
            <section style={{ padding: `0 ${px} 5rem`, maxWidth: 1200, margin: "0 auto" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.5rem", alignItems: "start" }}>
                    {TIERS.map(tier => (
                        <div key={tier.id} style={{ background: tier.bgColor, border: "1.5px solid " + tier.borderColor, borderRadius: 16, padding: "2rem", position: "relative", display: "flex", flexDirection: "column", boxShadow: tier.id === "pro" ? "0 4px 24px rgba(82,101,143,0.12)" : "none" }}>
                            {tier.badge && (
                                <div style={{ position: "absolute", top: "-0.75rem", left: "50%", transform: "translateX(-50%)", background: C.accent, color: "white", fontSize: "0.65rem", fontFamily: MONO, fontWeight: 700, padding: "0.25rem 0.85rem", borderRadius: 100, whiteSpace: "nowrap", letterSpacing: "0.06em" }}>
                                    {tier.badge}
                                </div>
                            )}

                            <div style={{ fontSize: "0.7rem", color: tier.accentColor, fontFamily: MONO, fontWeight: 700, letterSpacing: "0.1em", marginBottom: "0.75rem" }}>{tier.name.toUpperCase()}</div>
                            <div style={{ fontFamily: SYNE, fontSize: "2.4rem", fontWeight: 800, color: C.text, marginBottom: "0.2rem", letterSpacing: "-0.02em" }}>{tier.price}</div>
                            <div style={{ fontSize: "0.78rem", color: C.dim, marginBottom: "1.75rem", fontFamily: MONO }}>{tier.period}</div>

                            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", marginBottom: "1.75rem", flex: 1 }}>
                                {tier.features.map((f, i) => (
                                    <div key={i} style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
                                        <span style={{ color: tier.accentColor, fontSize: "0.85rem", marginTop: "0.1rem", flexShrink: 0, fontWeight: 700 }}>✓</span>
                                        <span style={{ fontSize: "0.85rem", color: C.muted, fontFamily: MONO }}>{f}</span>
                                    </div>
                                ))}
                                {tier.notIncluded.map((f, i) => (
                                    <div key={i} style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start", opacity: 0.4 }}>
                                        <span style={{ color: C.dim, fontSize: "0.85rem", marginTop: "0.1rem", flexShrink: 0 }}>✕</span>
                                        <span style={{ fontSize: "0.85rem", color: C.dim, fontFamily: MONO }}>{f}</span>
                                    </div>
                                ))}
                            </div>

                            <button onClick={() => handleCTA(tier)} style={{
                                width: "100%",
                                padding: "0.85rem",
                                borderRadius: 8,
                                fontFamily: MONO,
                                fontWeight: 700,
                                fontSize: "0.9rem",
                                cursor: "pointer",
                                border: tier.ctaVariant === "filled" ? "none" : "1.5px solid " + C.accent,
                                background: tier.ctaVariant === "filled" ? C.accent : "transparent",
                                color: tier.ctaVariant === "filled" ? "white" : C.accent,
                                transition: "opacity 0.15s",
                            }}
                                onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
                                onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
                                {tier.cta}
                            </button>
                        </div>
                    ))}
                </div>

                <div style={{ textAlign: "center", marginTop: "2rem", fontSize: "0.82rem", color: C.dim, fontFamily: MONO }}>
                    Questions? Email us at{" "}
                    <a href="mailto:hello@datagetsbalanced.com" style={{ color: C.accent, textDecoration: "none" }}>hello@datagetsbalanced.com</a>
                </div>
            </section>

            {/* FAQ */}
            <section style={{ padding: `0 ${px} 5rem`, maxWidth: 800, margin: "0 auto" }}>
                <h2 style={{ fontFamily: SYNE, fontSize: "1.85rem", fontWeight: 800, color: C.text, marginBottom: "2.5rem", textAlign: "center", letterSpacing: "-0.02em" }}>
                    Frequently asked questions
                </h2>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                    {FAQ.map((item, i) => (
                        <div key={i} onClick={() => setOpenFaq(openFaq === i ? null : i)}
                            style={{ background: openFaq === i ? C.surface : "white", border: "1px solid " + C.border, borderRadius: 12, padding: "1.25rem 1.5rem", cursor: "pointer", transition: "background 0.15s" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem" }}>
                                <div style={{ fontFamily: MONO, fontWeight: 700, color: C.text, fontSize: "0.9rem" }}>{item.q}</div>
                                <div style={{ color: C.accent, fontSize: "1rem", flexShrink: 0, fontWeight: 700 }}>{openFaq === i ? "−" : "+"}</div>
                            </div>
                            {openFaq === i && (
                                <div style={{ color: C.muted, fontSize: "0.85rem", lineHeight: 1.8, marginTop: "0.75rem", fontFamily: MONO }}>{item.a}</div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* Footer */}
            <footer style={{ background: "#000000", padding: "1.75rem 2rem", textAlign: "center" }}>
                <span style={{ color: "#555", fontSize: "0.8rem", fontFamily: MONO }}>
                    © 2026 DGB ·{" "}
                    <a href="mailto:hello@datagetsbalanced.com" style={{ color: "#555", textDecoration: "none" }}>hello@datagetsbalanced.com</a>
                    {" · "}Jakarta, Indonesia
                </span>
            </footer>
        </div>
    );
}
